import { getItemDB, setItemDB, getProjectKey } from './commons.js';
import R3fForceGraph from 'r3f-forcegraph';
import { forceCollide } from 'd3-force-3d';
import React, { useCallback, useRef, useEffect, useState, useLayoutEffect, useMemo } from 'react';
import { Canvas, useFrame, extend, useThree } from '@react-three/fiber';
import { Stats, Lightformer, CameraControls, OrbitControls, useBounds, Environment, Html } from '@react-three/drei';
import debounce from "lodash/debounce";
import { easing } from 'maath'
import { THREE, isChrome, DEG2RAD, __DEFAULT_SMOOTH_TIME, prefValue, HIGH } from './three/context.js';
import { FontLoader } from 'three/addons/loaders/FontLoader.js';
import './styles/index.css'
import SettingsOverlay from './SettingsOverlay.jsx';
import { severityColors } from './three/materials.js';
import { get3dText, geom, sphereMesh } from './three/objects.js';
import { hideNode, showAllNodesThree } from './three/visibility.js';
import { resetCamera, animateChangeRotation, animateChangeRotation2, cameraTarget, padding } from './three/camera.js';
import { makeExportImage } from './services/imageExport.js';
import { exportInstalledSoftwareCsv, exportSecurityBlastCsv } from './services/csvExport.js';
import { useGraphData } from './hooks/useGraphData.js';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts.js';
import { CveDetailCard } from './components/panels/CveDetailCard.jsx';
import { HostDetailCard } from './components/panels/HostDetailCard.jsx';
import { CveListPanel } from './components/panels/CveListPanel.jsx';
import { IconRail } from './components/panels/IconRail.jsx';
import { CvePanel } from './components/panels/CvePanel.jsx';
import { HostsPanel } from './components/panels/HostsPanel.jsx';
import { HostsList } from './components/panels/HostsList.jsx';
import { SeverityChips } from './components/panels/SeverityChips.jsx';
import { SpinnerOverlay } from './components/panels/SpinnerOverlay.jsx';
import { NoticeBanner } from './components/panels/NoticeBanner.jsx';
import { Legend } from './components/panels/Legend.jsx';
import { EmptyState } from './components/panels/EmptyState.jsx';
import { GraphViz } from './components/GraphViz.jsx';
import { CameraLookAt } from './components/CameraLookAt.jsx';
import { FrameLimiter } from './components/FrameLimiter.jsx';
import { LabelShapeSVG } from './components/LabelShapeSVG.jsx';
import { LoadingProgress } from './components/LoadingProgress.jsx';
import { APPF_securityBlast } from './components/icons/SecurityBlastIcon.jsx';
// Re-exported for main-app consumers (AddCredential.js imports LabelShapeSVG from 3d/src/App)
export { FrameLimiter } from './components/FrameLimiter.jsx';
export { LabelShapeSVG } from './components/LabelShapeSVG.jsx';
export { LoadingProgress } from './components/LoadingProgress.jsx';
import { colorsSeverity, chipTintDark, chipTintLight, severityTypes } from './constants/severity.js';
import { LEVEL3_EXPAND_THRESHOLD, LEVEL3_MAX_RENDER_NODES, LEVEL3_MAX_RENDER_LINKS, AGGREGATE_GROUPS, LIST_MAX_ROWS } from './constants/budgets.js';
import { javaColors, pieColors } from './constants/colors.js';
// Re-exported for main-app consumers (e.g. src/components/AddCredential.js imports from 3d/src/App)
export { javaColors, pieColors } from './constants/colors.js';


window.interval = -1;
window.currentNode = 0;
window.statsDataMap = {};
window.linkColors = {};
// True between a drill-navigation click and the moment the rebuilt graph data is handed to
// the force graph — engine-stop events in that window are stale and must not clear the
// loading state.
window.__graphRebuildPending = false;
window.nodeCache = {};


























const App = () => {
  const [position, setPosition] = useState([0, 0, 0])
  const [raycaster, setRaycaster] = useState(null)
  const [scene3, setScene3] = useState(null)
  const cameraControlsRef = useRef(null)
  const [isCVESearch, setIsCVESearch] = useState(localStorage.getItem("sb_uiState")?(`${JSON.parse(localStorage.getItem("sb_uiState")).isCVESearch}`==`true`): true)

  // console.log("3D Vulnerability Graph Loaded - CVE Search:", isCVESearch);
  const [secStat, setSecStat] = useState({});
  const [progress, setProgress] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [frameRate, setFrameRate] = useState(localStorage.getItem("frameRate") || 60);
  const [mouseSensitivity, setMouseSensitivity] = useState(localStorage.getItem("mouseSensitivity") || 0.1);

  // const [gDataFiltered, setGDataFiltered] = useState({nodes:[],links:[]});

  const inputRefCVE = useRef(null)
  const inputRefHosts = useRef(null)
  const canvas = useRef(null)
  const [gDataFiltered, setGDataFiltered] = useState({ nodes: [], links: [] });
  const [showHelp, setShowHelp] = useState(false);
  // Graph source data + IDB boot/live-reload moved to useGraphData; keyboard
  // shortcuts to useKeyboardShortcuts. Both called below, after isInstalledSoftware.

  // Cluster view: with very large vulnerability graphs (thousands+ of CVE nodes), rendering
  // every node individually freezes the tab (each is a separate three.js draw call). Instead,
  // group CVE nodes by package into a small number of cluster bubbles by default; clicking a
  // cluster drills into just that cluster's real CVE + host nodes via `selectedCluster`.
  const [selectedCluster, setSelectedCluster] = useState(null);

  // Level-2 drill-in: severity and/or host-group chosen inside a cluster (null = show the
  // aggregate sub-bubbles). Either one narrows the selection and expands it to real nodes.
  const [selectedSeverity, setSelectedSeverity] = useState(null);
  const [selectedHostGroup, setSelectedHostGroup] = useState(null);

  // Non-null while the rendered graph is a bounded top-N slice of a larger selection.
  const [renderNotice, setRenderNotice] = useState(null);

  // When the drilled-in graph is still too big to render as individual nodes, the graph shows
  // aggregate bubbles while the side lists/badges describe the real underlying selection here:
  // { cveNodes, hostNodes } of the current cluster(+severity). Null = lists mirror gDataFiltered.
  const [listData, setListData] = useState(null);

  const isInstalledSoftware = location.search.includes("installedSoftware");
  // Graph source data + IDB boot/live-reload live in useGraphData; must be called
  // before anything that derives from gData (clusterData memo below).
  const { gData, setGData, originalData, setOriginalData, hostsData, hostsDiscoveryMap } = useGraphData({ isInstalledSoftware });

  const clusterData = useMemo(() => {
    if (!gData?.nodes) return { nodes: [], links: [] };

    const severityRank = { critical: 0, high: 1, medium: 2, low: 3 };
    const clusters = {};

    gData.nodes.forEach(n => {
      if (!n.id.startsWith("CVE-")) return;

      const key = n.data?.vulnerability?.package || n.data?.vulnerability?.vendor || "unknown";
      const severity = n.data?.vulnerability?.severity?.toLowerCase() || "unknown";

      if (!clusters[key]) {
        clusters[key] = { clusterKey: key, count: 0, sevCounts: {} };
      }

      clusters[key].count++;
      clusters[key].sevCounts[severity] = (clusters[key].sevCounts[severity] || 0) + 1;
    });

    // Color = MOST COMMON severity in the package (per Diana) — "highest present"
    // made every real-world cluster red, so color carried no information. Ties
    // break toward the more severe one.
    Object.values(clusters).forEach(c => {
      c.dominantSeverity = Object.entries(c.sevCounts)
        .sort((a, b) => b[1] - a[1] || (severityRank[a[0]] ?? 99) - (severityRank[b[0]] ?? 99))[0][0];
    });

    // Size clusters RELATIVE to the biggest one — the old absolute formula capped at
    // val 24 almost immediately, so every real-world package rendered as the same
    // small dot and the size encoding carried no information.
    const maxCount = Math.max(1, ...Object.values(clusters).map(c => c.count));
    const nodes = Object.values(clusters).map(c => ({
      id: `cluster:${c.clusterKey}`,
      name: `${c.clusterKey} (${c.count.toLocaleString()})`,
      group: "cluster",
      clusterKey: c.clusterKey,
      val: 14 + 30 * Math.sqrt(c.count / maxCount),
      data: { vulnerability: { severity: c.dominantSeverity } },
    }));

    return { nodes, links: [] };
  }, [gData]);


 

  const [uiState, setUIState] = useState(localStorage.getItem("sb_uiState")?JSON.parse(localStorage.getItem("sb_uiState")):
  {
    advancedSearch: false,
    filter: "",
    detailsItem: "",
    activeNode: null,
    severityFilter: "",
  });

  const { filter, detailsItem, activeNode, severityFilter } = uiState;
  let detailsItemData = detailsItem && originalData ? originalData?.nodes.find(f => f.id == detailsItem) : {};



  const [dark] = useState(localStorage.getItem("themeKey") == "dark")
  const [font, setFont] = useState(null);


  useEffect(() => {
    // Active/hover row severity colors used to be generated here and injected into
    // <head>; they are now static CSS in styles/severity.css (theme handled by the
    // sb-dark/sb-light class on the root div).
    if (!window.__fontloaded) {
      const loader = new FontLoader();
      loader.load('font/Inter_Medium_Regular.json', function (font) {
        window.__fontloaded = font;
        setFont(font)
      })
    } else {
      setFont(window.__fontloaded)
    }
    let uiState = localStorage.getItem("sb_uiState")?JSON.parse(localStorage.getItem("sb_uiState")):null;

    
    if(uiState){
      // setTimeout(() => {
        if(uiState.isCVESearch){
          inputRefCVE.current.value = uiState.filter
        } else {
          inputRefHosts.current.value = uiState.filter
        }
      // }, 100)
    }
    
  }, [])

  useEffect(() => {
    window.__severityFilter = severityFilter
    setProgress(true)
  }, [severityFilter])




  // Severity badge counts (CRITICAL/HIGH/MEDIUM/LOW totals) reflect the whole dataset, not
  // just whatever's currently rendered — computed independently of cluster/filter state so
  // they stay populated even before the user has drilled into any cluster.
  useEffect(() => {
    if (!gData?.nodes) return;

    try {
      let hostsMap = {};
      let secData = gData.nodes.reduce((acc, f) => {
        let sev = f.data?.vulnerability?.severity?.toLowerCase();
        if (!sev) return acc;
        acc[sev] = (acc[sev] || 0) + 1;
        return acc;
      }, {});

      secData.CVE = [];
      secData.host = [];
      gData.nodes.forEach(f => {
        if (f.id.startsWith("CVE-")) {
          if (typeof window.linkColors[f.id] == "undefined") {
            window.linkColors[f.id] = f.data?.vulnerability?.severity?.toLowerCase();
          }

          secData.CVE.push(f);
          (f.data?.affectedHosts || []).forEach(h => {
            if (!hostsMap[h]) {
              hostsMap[h] = [f.id];
            } else if (!hostsMap[h].includes(f.id)) {
              hostsMap[h].push(f.id);
            }
          });
        } else {
          secData.host.push(f);
        }
      });

      secData.hostsMap = hostsMap;
      setSecStat(secData);
    } catch (e) {
      console.log("3D App: error computing secStat", e);
    }
  }, [gData, setSecStat]);

  useEffect(() => {
    // Everything below (re)builds the graph data; engine-stop events fired before the new
    // data is handed off are stale and must not clear the loading state.
    window.__graphRebuildPending = true;

    // Installed-software mode has no CVE/package structure, so clustering does not apply;
    // it always runs the plain filtering pipeline over the full graph like it did before.
    const isSW = location.search.includes("installedSoftware");

    // Clustering exists to protect the renderer at scale; a dataset that already fits the
    // level-3 node budget skips it entirely and renders the full graph directly (the
    // pre-clustering behavior) — package bubbles would just add a click. Falls through to
    // the legacy pipeline below with no cluster selected, so no cap applies.
    const smallDataset = (gData?.nodes?.length || 0) > 0 && gData.nodes.length <= LEVEL3_EXPAND_THRESHOLD;

    // Default to the cheap cluster view (one bubble per package) instead of rendering every
    // individual CVE/host node. Only compute the expensive per-node filtering pipeline below
    // once the user has drilled into a specific cluster.
    if (!selectedCluster && !isSW && !smallDataset) {
      // The graph shows package clusters, but the side lists/counters describe the whole
      // dataset (capped at LIST_MAX_ROWS rendered rows). The search boxes narrow the lists
      // here; the graph itself only changes once a cluster is drilled into.
      if (gData?.nodes) {
        let cveNodes = gData.nodes.filter(n => n.id.startsWith("CVE-"));
        let hostNodes = gData.nodes.filter(n => n.group === "host");
        if (filter != "") {
          const match = n => n.name?.toLowerCase().includes(filter.toLowerCase());
          if (isCVESearch) {
            cveNodes = cveNodes.filter(match);
          } else {
            hostNodes = hostNodes.filter(match);
          }
        }
        setListData({ cveNodes, hostNodes });
      } else {
        setListData(null);
      }
      setRenderNotice(null);
      window.__graphRebuildPending = false;
      if (gDataFiltered === clusterData) {
        // Already showing this exact cluster view (e.g. a severity badge or search changed):
        // the graph won't rebuild, so no engine-stop event will clear the loading state.
        setProgress(false);
      }
      setGDataFiltered(clusterData);
      return;
    }

    if (!gData) {
      window.__graphRebuildPending = false;
      return;
    }

    // Defer the rebuild one beat so the loading overlay paints before this blocks the main
    // thread (the spinner itself keeps animating on the compositor while it's blocked).
    const heavyT = setTimeout(() => {
    let gg = { nodes: [], links: [] };
    let map = [];

    let filterID = "cluster:" + selectedCluster + selectedSeverity + selectedHostGroup + filter + severityFilter + isCVESearch;
    {
      window.statsDataMap = {}
      if (typeof window.statsDataMap[filterID] != "undefined") {
        // console.log(window.statsDataMap[filterID].gg);
        window.__graphRebuildPending = false;
        setGDataFiltered(JSON.parse(window.statsDataMap[filterID].gg));
        return;
      }

      // The current selection: the cluster's CVEs (optionally narrowed to one severity, via
      // the level-2 severity bubbles or the header severity filter, and/or to one host group)
      // plus the hosts they affect. Computed on gData directly — read-only, no deep clone yet.
      const effectiveSeverity = selectedSeverity || (severityTypes.includes(severityFilter) ? severityFilter : "");
      const hostGroupOf = (hostId) => {
        const info = hostsDiscoveryMap?.[hostId];
        const tag = info?.tags?.[0];
        return (typeof tag === "string" && tag) || info?.os?.name || "ungrouped";
      };
      // severityTypes is ordered most→least severe; unknown severities sort last.
      const sevRank = (n) => {
        const i = severityTypes.indexOf(n.data?.vulnerability?.severity?.toLowerCase());
        return i === -1 ? severityTypes.length : i;
      };
      let selCveNodes = selectedCluster ? gData.nodes.filter(n =>
        n.id.startsWith("CVE-") &&
        (n.data?.vulnerability?.package || n.data?.vulnerability?.vendor || "unknown") === selectedCluster &&
        (effectiveSeverity === "" || n.data?.vulnerability?.severity?.toLowerCase() === effectiveSeverity)
      ) : [];
      let selHostIds = new Set();
      selCveNodes.forEach(n => (n.data?.affectedHosts || []).forEach(h => selHostIds.add(h)));
      if (selectedHostGroup) {
        selHostIds = new Set([...selHostIds].filter(h => hostGroupOf(h) === selectedHostGroup));
        selCveNodes = selCveNodes.filter(n => (n.data?.affectedHosts || []).some(h => selHostIds.has(h)));
      }

      const selectionSize = selCveNodes.length + selHostIds.size;
      const narrowed = effectiveSeverity !== "" || selectedHostGroup != null;

      // Level 2: an un-narrowed cluster that's too large to draw node-by-node renders as an
      // aggregate view — a hub for the package, one bubble per severity and one per host
      // group (tag/OS); clicking either narrows the selection. The side lists still show the
      // full underlying selection via listData.
      if (selectedCluster && filter == "" && !narrowed && selectionSize > LEVEL3_EXPAND_THRESHOLD) {
        const sevCounts = {};
        selCveNodes.forEach(n => {
          const s = n.data?.vulnerability?.severity?.toLowerCase() || "unknown";
          sevCounts[s] = (sevCounts[s] || 0) + 1;
        });
        const dominantSeverity = severityTypes.find(s => sevCounts[s]) || "unknown";

        const hubId = `pkg:${selectedCluster}`;
        const hubSeverity = selectedSeverity || dominantSeverity;
        const aggNodes = [{
          id: hubId,
          name: selectedSeverity ? `${selectedCluster} · ${selectedSeverity} (${selCveNodes.length.toLocaleString()})` : `${selectedCluster} (${selCveNodes.length.toLocaleString()})`,
          group: "clusterHub",
          clusterKey: selectedCluster,
          val: 40,
          data: { vulnerability: { severity: hubSeverity } },
        }];
        const aggLinks = [];
        // linkMaterial colors every hub-spoke link by the hub's id (links never point at a
        // CVE-* id here), so it needs an entry in linkColors or it throws on a missing material.
        window.linkColors[hubId] = hubSeverity;

        // Like the level-1 clusters: size RELATIVE to the biggest bubble of each
        // family (severities vs host groups count different things), so the size
        // encoding survives any data scale instead of capping into uniform dots.
        if (!selectedSeverity) {
          const maxSev = Math.max(1, ...Object.values(sevCounts));
          severityTypes.forEach(s => {
            if (!sevCounts[s]) return;
            const id = `sev:${selectedCluster}:${s}`;
            aggNodes.push({
              id,
              name: `${s} (${sevCounts[s].toLocaleString()})`,
              group: "severityGroup",
              severityKey: s,
              clusterKey: selectedCluster,
              val: 12 + 22 * Math.sqrt(sevCounts[s] / maxSev),
              data: { vulnerability: { severity: s } },
            });
            aggLinks.push({ source: hubId, target: id });
          });
        }

        const hostGroups = {};
        selHostIds.forEach(h => {
          const key = hostGroupOf(h);
          hostGroups[key] = (hostGroups[key] || 0) + 1;
        });
        const maxHostGroup = Math.max(1, ...Object.values(hostGroups));
        Object.entries(hostGroups).forEach(([key, count]) => {
          const id = `hosts:${selectedCluster}:${key}`;
          aggNodes.push({
            id,
            name: `${key} (${count.toLocaleString()} hosts)`,
            group: "hostGroup",
            hostGroupKey: key,
            val: 12 + 22 * Math.sqrt(count / maxHostGroup),
          });
          aggLinks.push({ source: hubId, target: id });
        });

        setListData({
          cveNodes: selCveNodes,
          hostNodes: gData.nodes.filter(n => n.group === "host" && selHostIds.has(n.id)),
        });
        setRenderNotice(null);
        window.__graphRebuildPending = false;
        setGDataFiltered({ nodes: aggNodes, links: aggLinks });
        return;
      }

      // Level 3: the user narrowed the selection (severity/host-group click or search) or it
      // was small to begin with — render real CVE/host nodes. The greedy pass below always
      // runs: a selection can be under the node budget yet carry far more links than the
      // link budget (high blast-radius data: ~450 nodes can mean ~8k lines), so both are
      // enforced. When something is cut, draw only the top CVEs by blast radius and say so;
      // the side lists keep describing the full selection.
      let renderCveNodes = selCveNodes;
      let renderHostIds = selHostIds;
      let notice = null;
      if (selectedCluster && filter == "") {
        const sorted = [...selCveNodes].sort((a, b) =>
          (b.data?.affectedHosts?.length || 0) - (a.data?.affectedHosts?.length || 0) || sevRank(a) - sevRank(b));
        renderCveNodes = [];
        renderHostIds = new Set();
        let renderLinks = 0;
        for (const n of sorted) {
          const hs = (n.data?.affectedHosts || []).filter(h => selHostIds.has(h));
          const newHosts = hs.filter(h => !renderHostIds.has(h)).length;
          if (renderCveNodes.length + 1 + renderHostIds.size + newHosts > LEVEL3_MAX_RENDER_NODES) break;
          if (renderLinks + hs.length > LEVEL3_MAX_RENDER_LINKS) break;
          renderCveNodes.push(n);
          renderLinks += hs.length;
          hs.forEach(h => renderHostIds.add(h));
        }
        if (renderCveNodes.length < selCveNodes.length) {
          notice = `Graph shows top ${renderCveNodes.length} of ${selCveNodes.length} CVEs by affected hosts — search or filter to narrow further`;
        } else {
          renderCveNodes = selCveNodes;
          renderHostIds = selHostIds;
        }
      }
      setRenderNotice(notice);
      setListData(notice ? {
        cveNodes: selCveNodes,
        hostNodes: gData.nodes.filter(n => n.group === "host" && selHostIds.has(n.id)),
      } : null);

      let ggOrg = JSON.parse(JSON.stringify(gData));
      // ggOrg.nodes = ggOrg.nodes.filter(f=>f?.data?.affectedHosts?.length>1)

      // Scope to only the selected cluster's CVEs + the hosts they affect, before running
      // the existing severity/search filtering pipeline on this much smaller subset.
      if (selectedCluster) {
        const clusterCveIds = new Set(renderCveNodes.map(n => n.id));
        const clusterHostIds = renderHostIds;
        ggOrg.nodes = ggOrg.nodes.filter(n => clusterCveIds.has(n.id) || clusterHostIds.has(n.id));
        ggOrg.links = ggOrg.links.filter(l => {
          const s = l.source?.id || l.source;
          const t = l.target?.id || l.target;
          return (clusterCveIds.has(s) || clusterHostIds.has(s)) && (clusterCveIds.has(t) || clusterHostIds.has(t));
        });
      }

      gg = ggOrg;

      // Severity badge counts are computed independently of this filtering pipeline (see the
      // `secStat` effect above, driven by the full `gData`) so they don't get clobbered by
      // whichever cluster-scoped subset happens to be selected here.
      ggOrg.nodes.forEach(f => {
        if (f.id.startsWith("CVE-") && typeof window.linkColors[f.id] == "undefined") {
          window.linkColors[f.id] = f.data?.vulnerability?.severity?.toLowerCase();
        }
      });

      let hosts = gg.nodes.filter(f => {
        return f.group == "host";
      })

      if (severityTypes.includes(severityFilter)) {
        gg.nodes = ggOrg.nodes.filter(f => {
          let res = f.data?.vulnerability?.severity?.toLowerCase() == severityFilter;

          if (res) {
            map.push(f.name);
            map = map.concat(f.data?.affectedHosts);
          }
          return res;
        })

        hosts = hosts.filter(f => {
          return map.includes(f.id);
        })

        gg.nodes = gg.nodes.concat(hosts);
      }

      if (filter == "") {
        gg.nodes.map(f => {
          if (!map.includes(f)) {
            map.push(f.id);
          }
        })

        // map = map.filter((v, i, a) => a.indexOf(v) === i)

        gg.nodes = gg.nodes.filter((arr, index, self) =>
          index === self.findIndex((t) => (t.id === arr.id && t.name === arr.name)))


        gg.links = gg.links.filter(f => {
          if (f.source?.id) {
            return (map.includes(f.source?.id) && map.includes(f.target?.id) || (map.includes(f.target?.id) && map.includes(f.source?.id)));
          } else {
            return (map.includes(f.source) && map.includes(f.target) || (map.includes(f.target) && map.includes(f.source)));
          }
        });

      } else {

        if(isCVESearch){
        }
        
        map = []
        const filterFunc = (f => {
          if(uiState?.advancedSearch){
            let foundMatch = false;
            for (let k in f){
              if(`${f[k]}`.toLowerCase()?.indexOf(filter.toLowerCase()) != -1){
                foundMatch = true;
                break;
              }
            }

            return foundMatch;
          }else if (f?.name?.toLowerCase()?.indexOf(filter.toLowerCase()) != -1
            //|| f?.id?.indexOf(filter) != -1
          ) {
            return true
          } else {
            return false
          }
        });

        let allHosts = gg.nodes.filter(f=> !f.id.startsWith("CVE-") );

        gg.nodes = gg.nodes
        .filter(f=> isCVESearch?f.id.startsWith("CVE-") : !f.id.startsWith("CVE-") )
        .filter(filterFunc);

        let newNodes = [];

        gg.nodes.map(f => {
          map.push(f.id);
          if (isCVESearch) {

            if (f.data?.affectedHosts) {
              map = map.concat(f.data?.affectedHosts);
            }
          } else {
            if (secStat?.hostsMap && secStat?.hostsMap[f.id]) {
              secStat?.hostsMap[f.id]?.map(h => {
                let n = gData.nodes.find(n => n.id == h);

                if (severityFilter == "") {
                  newNodes.push(n);
                  map.push(h);
                } else {
                  if (n.data?.vulnerability?.severity?.toLowerCase() == severityFilter) {
                    newNodes.push(n);
                    map.push(h);
                  }
                }
              })
            }
          }
        })


        gg.nodes = gg.nodes.concat(newNodes);

        map = map.filter((v, i, a) => a.indexOf(v) === i)

        // let hostsFiltered = hosts.filter(filterFunc)

        hosts = hosts.filter(f => {
          return map.includes(f.id);
        })

        gg.nodes = gg.nodes.concat(hosts);

        gg.nodes = gg.nodes.filter((arr, index, self) =>
          index === self.findIndex((t) => (t.id === arr.id && t.name === arr.name)))




        // try{
        //   if(gg.links[0].source.id){
        //     isAddedLink = true;
        //     // console.log(isAddedLink+" ----- IS ADDED LINK")
        //   }
        // }catch(e){/**/}

        gg.links = gg.links.filter(f => {

          if (f.source?.id) {
            return (map.includes(f.source?.id) && map.includes(f.target?.id) || (map.includes(f.target?.id) && map.includes(f.source?.id)));
          } else {
            return (map.includes(f.source) && map.includes(f.target) || (map.includes(f.target) && map.includes(f.source)));
          }
        });

        // console.log(gg.links , " ---- GG LINKS")

        // A broad search inside a big cluster can match tens of thousands of nodes; cap the
        // rendered set the same way the un-searched level-3 path is capped, keeping the
        // widest-blast / most-severe CVEs. The side lists keep the full matched set. Links
        // are budgeted alongside nodes — high blast-radius matches can blow the line budget
        // long before the node one.
        if (selectedCluster && (gg.nodes.length > LEVEL3_MAX_RENDER_NODES || gg.links.length > LEVEL3_MAX_RENDER_LINKS)) {
          const matchedCves = gg.nodes.filter(f => f.id.startsWith("CVE-"));
          const matchedHostNodes = gg.nodes.filter(f => !f.id.startsWith("CVE-"));
          const matchedHostIds = new Set(matchedHostNodes.map(f => f.id));
          const sorted = [...matchedCves].sort((a, b) =>
            (b.data?.affectedHosts?.length || 0) - (a.data?.affectedHosts?.length || 0) || sevRank(a) - sevRank(b));
          const keptCveIds = new Set();
          const keptHostIds = new Set();
          let keptLinks = 0;
          for (const n of sorted) {
            const hs = (n.data?.affectedHosts || []).filter(h => matchedHostIds.has(h));
            const newHosts = hs.filter(h => !keptHostIds.has(h)).length;
            if (keptCveIds.size + 1 + keptHostIds.size + newHosts > LEVEL3_MAX_RENDER_NODES) break;
            if (keptLinks + hs.length > LEVEL3_MAX_RENDER_LINKS) break;
            keptCveIds.add(n.id);
            keptLinks += hs.length;
            hs.forEach(h => keptHostIds.add(h));
          }
          gg.nodes = gg.nodes.filter(f => keptCveIds.has(f.id) || keptHostIds.has(f.id));
          gg.links = gg.links.filter(l => {
            const s = l.source?.id || l.source;
            const t = l.target?.id || l.target;
            return (keptCveIds.has(s) || keptHostIds.has(s)) && (keptCveIds.has(t) || keptHostIds.has(t));
          });
          setListData({ cveNodes: matchedCves, hostNodes: matchedHostNodes });
          setRenderNotice(`Graph shows top ${keptCveIds.size} of ${matchedCves.length} matching CVEs — narrow the search to see more`);
        }
      }

      // Un-clustered views (small-dataset bypass) still respect the line budget, but
      // without dropping anything: every CVE and host keeps at least one connection (no
      // node floats unlinked), and the remaining budget goes to the top CVEs (blast
      // radius, then severity) as full fan-outs. The bypass guarantees CVEs + hosts ≤
      // LEVEL3_EXPAND_THRESHOLD, so the one-line-per-node passes always fit the budget.
      // The side lists and severity badges are unaffected.
      if (!selectedCluster && !isSW && gg.links.length > LEVEL3_MAX_RENDER_LINKS) {
        const srcOf = l => l.source?.id || l.source;
        const tgtOf = l => l.target?.id || l.target;
        const cves = gg.nodes.filter(f => f.id.startsWith("CVE-"));
        const sorted = [...cves].sort((a, b) =>
          (b.data?.affectedHosts?.length || 0) - (a.data?.affectedHosts?.length || 0) || sevRank(a) - sevRank(b));
        const linksBySrc = {};
        gg.links.forEach(l => { (linksBySrc[srcOf(l)] = linksBySrc[srcOf(l)] || []).push(l); });

        const kept = new Set();
        const coveredHosts = new Set();
        let budget = LEVEL3_MAX_RENDER_LINKS;
        // one line per CVE, preferring a host that has none yet
        for (const n of sorted) {
          if (budget <= 0) break;
          const ls = linksBySrc[n.id] || [];
          const pick = ls.find(l => !coveredHosts.has(tgtOf(l))) || ls[0];
          if (pick) { kept.add(pick); coveredHosts.add(tgtOf(pick)); budget--; }
        }
        // one line for any host still without one
        for (const l of gg.links) {
          if (budget <= 0) break;
          if (!coveredHosts.has(tgtOf(l))) { kept.add(l); coveredHosts.add(tgtOf(l)); budget--; }
        }
        // remaining budget: full fan-out for the top CVEs
        let fullFanOuts = 0;
        for (const n of sorted) {
          if (budget <= 0) break;
          let complete = true;
          for (const l of (linksBySrc[n.id] || [])) {
            if (kept.has(l)) continue;
            if (budget <= 0) { complete = false; break; }
            kept.add(l); budget--;
          }
          if (complete) fullFanOuts++;
        }
        const total = gg.links.length;
        gg.links = gg.links.filter(l => kept.has(l));
        setRenderNotice(`Drawing ${gg.links.length} of ${total} connections — every node keeps one, the top ${fullFanOuts} CVEs show all — search or filter for the rest`);
      }

      let index = 0;
      let count = 0;

      // gDataFiltered.nodes = gDataFiltered.nodes.map((f,index)=>{
      //   if(count %4 ==0 ){
      //     // index++;
      //   }
      //   if(f.group == "host"){
      //     // f.fx = 0
      //     // f.fx = 0 + index * 1.2
      //     // f.fz = 0
      //   }
      //   count++;
      //   return f;
      // })
      // gg.nodes = gg.nodes.filter((arr, index, self) =>
      //   index === self.findIndex((t) => (t.id === arr.id && t.name === arr.name)))

      // console.log(gg,map)
      // setProgress(true)


      if (typeof window.statsDataMap[filterID] == "undefined") {
        window.statsDataMap[filterID] = {
          gg: JSON.stringify(gg),
          cveCount: gg.nodes.filter(f => f.id.startsWith("CVE-")).length,
          hostsCount: gg.nodes.filter(f => !f.id.startsWith("CVE-")).length
        }
      }

      window.__graphRebuildPending = false;
      setGDataFiltered(gg)
      // setGDataFiltered2(gg)
    }//gdata
    }, 60);

    return () => clearTimeout(heavyT);
  }, [gData, setGDataFiltered, severityFilter, filter, isCVESearch, selectedCluster, selectedSeverity, selectedHostGroup, clusterData, hostsDiscoveryMap])

  // Solid enough that panel text stays readable over a bright zoomed-in graph;
  // the stronger blur smooths out node dots/labels bleeding through the glass.
  const backdropFilter = "blur(12px)"
  const backgroundColor = dark ? "rgba(24, 24, 27, 0.82)" : "rgba(42, 42, 42, 0.162)"
  const color = dark ? "white" : "black"
  // Card border matching the main app's --color-border-color token
  const panelBorder = dark ? "1px solid rgba(255,255,255,0.142)" : "1px solid rgba(0,0,0,0.1)"

  const onChange = (e) => {
    let value = e.target.value;

    try {
      value = value.replace(/^[\s\t]+|[\s\t]+$/g, "");
    } catch (e) {/**/ }

    setUIState(p => ({ ...p, filter: value }))
    setTimeout(() => {
      animateChangeRotation()
    }, 100)
  }


  const [animation, setAnimation] = useState(false);

  useEffect(() => {
    if (animation) {
      window.playAnimation = true
      animateChangeRotation2();
      let lastWas = "";
      function animateNextNode() {


        if (window.playAnimation == false) {
          clearInterval(interval);
          return;
        }

        if (!window.forceGraph) {
          for (let i = 0; i < window.scene3.children.length; i++) {
            if (window.scene3.children[i].type == "Group") {
              window.forceGraph = window.scene3.children[i]
              break
            }
          }
        }

        if (window.camera3.current) {
          if (currentNode > window.forceGraph.children.length) {
            currentNode = 0;
          }

          let anode = window.forceGraph.children[currentNode++]
          while (anode.__graphObjType != "node" || anode?.__data?.group == "host" || anode.visible == "false") {
            if (currentNode > window.forceGraph.children.length) {
              currentNode = 0;
            }
            anode = window.forceGraph.children[currentNode];
            currentNode++;
          }
          if (anode.__data.group == "host") {
            setUIState(p => ({ ...p, detailsItem: "" }))
          } else {
            setUIState(p => ({ ...p, detailsItem: anode.__data.name }))
          }

          if (anode.__data.group != "host") {
            let zNode = anode.__data

            let visibleList = [zNode.name];

            if (zNode?.data?.affectedHosts) {
              visibleList = visibleList.concat(zNode?.data?.affectedHosts)
            }

            for (let i = 0; i < window.forceGraph.children.length; i++) {
              let child = window.forceGraph.children[i]
              if (child.isLine) {
                if (child.__data.source.id == zNode.name && visibleList.includes(child.__data.target.id)) {
                  hideNode(child, false);
                } else {
                  hideNode(child, true);
                }
              }
            }
          }
          animateChangeRotation2();
        }
      }
      animateNextNode();
      if (window.interval != -1) {
        clearInterval(window.interval);
      }
      window.interval = setInterval(() => {
        animateNextNode()
      }, 5000);
    } else {
      window.playAnimation = false
      clearInterval(window.interval);
      window.interval = -1;
    }
  }, [animation]);

  const onChangeDebounced = debounce(onChange, 2000)
  // console.log("APP", window.interval)

  const [exportFunction, setExportFunction] = useState(null);
  // The side lists and counts describe the underlying selection: in the aggregated level-2
  // view that's listData (the real CVEs/hosts behind the bubbles); otherwise it's whatever
  // real nodes are actually rendered.
  const listCveNodes = listData ? listData.cveNodes : gDataFiltered.nodes.filter(f => f.id.startsWith("CVE-"));
  const listHostNodes = listData ? listData.hostNodes : gDataFiltered.nodes.filter(f => !f.id.startsWith("CVE-") && !AGGREGATE_GROUPS.includes(f.group));
  const cveCount = listCveNodes.length;
  const hostsCount = listHostNodes.length;


  const filteredSeverity = Object.groupBy((listData ? listData.cveNodes : gDataFiltered.nodes.filter(f => !AGGREGATE_GROUPS.includes(f.group))).filter(f => f.data?.vulnerability?.severity?.toLowerCase()),
    (node) => {
      let severity = node.data?.vulnerability?.severity?.toLowerCase();

      if (severityTypes.includes(severity)) {
        return severity;
      } else {
        return "unknown";
      }
    });

  const hostDetailsItem = detailsItem && !detailsItem.startsWith("CVE-") ? detailsItem : "";
  // An explicit user choice always wins; otherwise big graphs default to low
  // performance (10fps cap) so they don't freeze the tab at scale.
  const storedPerformance = localStorage.getItem(prefValue);
  const bigGraph = (gData?.nodes?.length || 0) > LEVEL3_EXPAND_THRESHOLD;
  const highPerformance = storedPerformance ? storedPerformance == HIGH : !bigGraph;
  const [context, setContext] = useState(null);
  const refSb = useRef(null);

  useKeyboardShortcuts({ setUIState, setProgress, setAnimation, inputRefCVE, inputRefHosts });
  const [widthRepair, setWidthRepair] = useState(0);
  useEffect(() => {
    if(widthRepair == 0 ){
      setTimeout(() => {
        setWidthRepair(1);
      }, 100);
    }
  }, []);

  return <div
    ref={refSb}
    className={"sbClass noScroll " + (dark ? "sb-dark" : "sb-light")}
    style={{
      width: widthRepair? "100%" : "calc(100% - 1px)",
      minWidth: "1200px",
      color: color,
      margin: 0, marginLeft: 0, padding: 0,
      border: "0px solid red", height: "calc(100vh - 0px)"
    }}>

    {showSettings &&
      <SettingsOverlay
        uiState={uiState}
        setUIState={setUIState}
        isCVESearch={isCVESearch}
        setShowSettings={setShowSettings}
        frameRate={frameRate}
        setFrameRate={setFrameRate}
        mouseSensitivity={mouseSensitivity}
        setMouseSensitivity={setMouseSensitivity}
        dark={dark}
        highPerformance={highPerformance}
      />}
      <div
      className="noScroll"

      style={{

        zIndex: 111,
        padding: 0,
        margin: 0,
        textAlign: "center",
        width: "calc(100% - 20px)",
        position: 'absolute',
      }}>
      <IconRail dark={dark} color={color} backgroundColor={backgroundColor} backdropFilter={backdropFilter} panelBorder={panelBorder} uiState={uiState} setUIState={setUIState} isCVESearch={isCVESearch} filter={filter} detailsItem={detailsItem} activeNode={activeNode} severityFilter={severityFilter} animation={animation} setAnimation={setAnimation} exportFunction={exportFunction} gData={gData} hostsData={hostsData} gDataFiltered={gDataFiltered} isInstalledSoftware={isInstalledSoftware} highPerformance={highPerformance} originalData={originalData} />

      <CvePanel dark={dark} color={color} backgroundColor={backgroundColor} backdropFilter={backdropFilter} panelBorder={panelBorder} progress={progress} setUIState={setUIState} isCVESearch={isCVESearch} setIsCVESearch={setIsCVESearch} filter={filter} detailsItem={detailsItem} activeNode={activeNode} severityFilter={severityFilter} cveCount={cveCount} inputRefCVE={inputRefCVE} inputRefHosts={inputRefHosts} onChange={onChange} onChangeDebounced={onChangeDebounced} isInstalledSoftware={isInstalledSoftware} />

      <CveDetailCard dark={dark} color={color} backgroundColor={backgroundColor} backdropFilter={backdropFilter} panelBorder={panelBorder} setUIState={setUIState} filter={filter} detailsItem={detailsItem} detailsItemData={detailsItemData} />

      <HostDetailCard dark={dark} color={color} backgroundColor={backgroundColor} backdropFilter={backdropFilter} panelBorder={panelBorder} setUIState={setUIState} detailsItem={detailsItem} hostsData={hostsData} detailsItemData={detailsItemData} hostDetailsItem={hostDetailsItem} />

      <CveListPanel dark={dark} color={color} backgroundColor={backgroundColor} backdropFilter={backdropFilter} setProgress={setProgress} setUIState={setUIState} isCVESearch={isCVESearch} setIsCVESearch={setIsCVESearch} filter={filter} detailsItem={detailsItem} activeNode={activeNode} listCveNodes={listCveNodes} cveCount={cveCount} inputRefCVE={inputRefCVE} inputRefHosts={inputRefHosts} selectedCluster={selectedCluster} setSelectedCluster={setSelectedCluster} selectedSeverity={selectedSeverity} setSelectedSeverity={setSelectedSeverity} selectedHostGroup={selectedHostGroup} setSelectedHostGroup={setSelectedHostGroup} isInstalledSoftware={isInstalledSoftware} />

      <HostsPanel dark={dark} color={color} backgroundColor={backgroundColor} backdropFilter={backdropFilter} panelBorder={panelBorder} setUIState={setUIState} isCVESearch={isCVESearch} setIsCVESearch={setIsCVESearch} filter={filter} detailsItem={detailsItem} activeNode={activeNode} severityFilter={severityFilter} hostsCount={hostsCount} inputRefCVE={inputRefCVE} inputRefHosts={inputRefHosts} onChange={onChange} onChangeDebounced={onChangeDebounced} isInstalledSoftware={isInstalledSoftware} />
      <SpinnerOverlay progress={progress} color={color} />
      <NoticeBanner renderNotice={renderNotice} color={color} backgroundColor={backgroundColor} backdropFilter={backdropFilter} panelBorder={panelBorder} />
      <Legend isInstalledSoftware={isInstalledSoftware} isClusterView={gDataFiltered === clusterData} isAggregateView={gDataFiltered?.nodes?.[0]?.group === "clusterHub"} dark={dark} color={color} backgroundColor={backgroundColor} backdropFilter={backdropFilter} panelBorder={panelBorder} />
      <EmptyState hostsCount={hostsCount} cveCount={cveCount} gDataFiltered={gDataFiltered} color={color} backgroundColor={backgroundColor} />
      <HostsList dark={dark} color={color} backgroundColor={backgroundColor} backdropFilter={backdropFilter} setUIState={setUIState} isCVESearch={isCVESearch} setIsCVESearch={setIsCVESearch} filter={filter} detailsItem={detailsItem} secStat={secStat} listHostNodes={listHostNodes} hostsCount={hostsCount} inputRefCVE={inputRefCVE} inputRefHosts={inputRefHosts} isInstalledSoftware={isInstalledSoftware} />


      <SeverityChips dark={dark} color={color} backgroundColor={backgroundColor} backdropFilter={backdropFilter} panelBorder={panelBorder} progress={progress} uiState={uiState} setUIState={setUIState} isCVESearch={isCVESearch} filter={filter} severityFilter={severityFilter} secStat={secStat} filteredSeverity={filteredSeverity} selectedCluster={selectedCluster} isInstalledSoftware={isInstalledSoftware} setShowSettings={setShowSettings} />



    </div>

    {/* alpha: true
        antialias: true
        canvas: true  
        powerPreference: "high-performance" */}
    {font && <Canvas
      fallback={<div>WebGL not supported!</div>}
      ref={canvas}
      flat
      // gl={{
      //   preserveDrawingBuffer: true
      // }}
      gl={(canvas) => {

        if (isChrome) {
          // Singleton per page: r3f re-invokes this factory whenever the Canvas
          // reconfigures while a previous (slow) `renderer.init()` is still pending —
          // on slow GPUs (Windows/D3D dev) that created 2-3 renderers fighting over
          // one canvas, the losers stuck at the 300×150 default and failing every
          // render pass ("attachment size does not match", graph never draws).
          // Reproduced locally by delaying requestAdapter 3s → 3 factory calls.
          // The page is a throwaway iframe and the Canvas mounts once, so caching
          // the init promise on window is safe.
          if (!window.__sbRendererPromise) {
            window.__sbRendererPromise = (async () => {
              let powerPreference = localStorage.getItem(prefValue) || HIGH;
              window.powerPreference = powerPreference;
              canvas.preserveDrawingBuffer = true
              // high-performance | low-power | default
              canvas.powerPreference = powerPreference;
              canvas.antialias = powerPreference == HIGH ? true : false;
              canvas.alpha = powerPreference == HIGH ? true : false;

              const renderer = new THREE.WebGPURenderer(canvas)
              await renderer.init()
              renderer.setAttribute = () => { }
              return renderer
            })();
          }
          return window.__sbRendererPromise
        }
      }
      }
      contols={{
        zoom: 0.9,
        zoomSpeed: localStorage.getItem("mouseSensitivity") || 0.1
      }}
      camera={{ zoom: 1, far: 100000, fov: 45, position: [0, 0, -1200] }}
      onCreated={({ gl, raycaster, scene, camera, invalidate }) => {

        setContext({ gl, scene, camera, invalidate });

        // The WebGPU renderer inits asynchronously; if r3f's initial setSize lands
        // during that window (seen on Windows/D3D under rapid page navigation), the
        // renderer's internal targets stay at the fresh-canvas 300×150 default while
        // the swapchain tracks the real canvas — every render pass then fails
        // validation ("attachment size does not match") and the graph never draws.
        // Heal ONLY on detected mismatch (an unconditional setSize + render here
        // races r3f's own configure before the pixel ratio applies and CAUSES the
        // same errors on a healthy boot), but keep watching for the renderer's
        // whole lifetime: on slow D3D machines the mismatch can appear later than
        // any fixed post-init delay, or while the canvas is still 0-sized.
        const healRendererSize = (hard) => {
          const parent = gl.domElement?.parentElement;
          if (!parent || !parent.clientWidth || !parent.clientHeight) return;
          const size = gl.getSize(new THREE.Vector2());
          const healthy = Math.abs(size.x - parent.clientWidth) <= 1 && Math.abs(size.y - parent.clientHeight) <= 1;
          if (!healthy || hard) {
            console.info(`SecurityBlast: healing renderer size ${size.x}x${size.y} -> ${parent.clientWidth}x${parent.clientHeight}${hard ? " (hard, after GPU validation error)" : ""}`);
            // A hard heal bounces through a different size first so every cached
            // render target (MSAA/depth) is destroyed and rebuilt, not size-matched.
            if (hard) gl.setSize(2, 2, false);
            gl.setSize(parent.clientWidth, parent.clientHeight, false);
            invalidate();
          }
          // A stale/degenerate camera aspect (r3f measured a 0-sized container while
          // the slow async init was pending) draws successfully but projects nothing —
          // blank canvas, no errors, "fixed by resizing the window". Repair it too.
          const wantAspect = parent.clientWidth / parent.clientHeight;
          if (camera?.isPerspectiveCamera && (!Number.isFinite(camera.aspect) || Math.abs(camera.aspect - wantAspect) > 0.01)) {
            console.info(`SecurityBlast: healing camera aspect ${camera.aspect} -> ${wantAspect.toFixed(3)}`);
            camera.aspect = wantAspect;
            camera.updateProjectionMatrix();
            invalidate();
          }
        };
        clearInterval(window.__sbSizeWatchdog);
        window.__sbSizeWatchdog = setInterval(() => healRendererSize(false), 1000);
        // getSize can report healthy while a stale cached target keeps failing
        // validation — the device's own error event is the ground truth for that.
        let lastHardHeal = 0;
        gl.backend?.device?.addEventListener?.("uncapturederror", (e) => {
          const msg = e?.error?.message || "";
          if (!/does not match the size|Invalid CommandBuffer/i.test(msg)) return;
          if (Date.now() - lastHardHeal < 4000) return;
          lastHardHeal = Date.now();
          healRendererSize(true);
        });

        gl.setClearColor(dark ? "rgb(2,2,2)" : "white", 1);
        // setRaycaster(raycaster);
        window.scene3 = scene;
        window.THREE_SCENE = scene;
        window.THREE_CAMERA = camera;
        window.THREE_GL = gl;
        window.THREE_CAMERA_ZOOM = camera.zoom;
        window.THREE_CAMERA_FAR = camera.far;
        window.THREE_CAMERA_FOV = camera.fov;

        setScene3(scene)
      }}>

      <CameraLookAt position={position} setPosition={setPosition} scene3={scene3} activeNode={activeNode} />
      <ambientLight color={dark ? 0xAAAAAA : 0x000000} intensity={dark ? 13.142 : 0.142} />
      <directionalLight intensity={0.2 * 1.14} />

      {false &&
        <Environment resolution={256}>

          <group rotation={[-Math.PI / 3, 0, 1]}>
            <Lightformer form="circle" intensity={4} rotation-x={Math.PI / 2} position={[0, 5, -9]} scale={2} />
            <Lightformer form="circle" intensity={2} rotation-y={Math.PI / 2} position={[-5, 1, -1]} scale={2} />
            <Lightformer form="circle" intensity={2} rotation-y={Math.PI / 2} position={[-5, -1, -1]} scale={2} />
          </group>


        </Environment>}



      {font && <GraphViz
        isInstalledSoftware={isInstalledSoftware}
        // progress={progress}
        setProgress={setProgress}
        secStat={secStat}
        setExportFunction={setExportFunction}
        animation={animation}
        font={font}
        dark={dark}
        gDataFiltered={gDataFiltered}
        setGDataFiltered2={setGDataFiltered}
        canvas={canvas}
        raycaster={raycaster}
        scene3={scene3}
        detailsItem={detailsItem}
        setUIState={setUIState}
        selectedCluster={selectedCluster}
        setSelectedCluster={setSelectedCluster}
        setSelectedSeverity={setSelectedSeverity}
        setSelectedHostGroup={setSelectedHostGroup}
      // setPosition={setPosition}
      // activeNode={activeNode}
      />
      }



      {/* </Fisheye> */}
      {/* <Stats></Stats> */}
      {context && (
        <FrameLimiter
          fps={highPerformance ? localStorage.getItem("frameRate") || 60 : 10}
          gl={context.gl}
          scene={context.scene}
          camera={context.camera}
          invalidate={context.invalidate}
        />
      )}
    </Canvas>}
  </div>;
};


export default App
